<template>
	<div class="content-page content-page--forms-history">
		<ActionSetNav
			v-if="navigatingFromCustomer"
			:name="navbarTitle"
			:display="true"
			:actions="mainNavActions"
			ref="customerNav"
		/>
		<div class="content-page__heading">
			<div class="content-page__breadcrumb-title">
				<Breadcrumbs :items="urlNames" />
				<h2 v-if="form" class="content-page__title">
					History
					<span class="content-page__title--detail">
						- {{ this.form.name }}
					</span>
				</h2>
			</div>
		</div>
		<div class="results-toolbar">
			<HistoryFilters
				v-model="filters"
				:defaultFilters="defaultFilters"
				:userOptions="users || []"
			/>
			<div class="results-toolbar__counter">
				<ResultCounter :counter="resultCounter" v-if="!loading" />
			</div>
		</div>
		<Spinner :processor="{ loading: true }" v-if="loading" />
		<ScrollContainer v-if="hasResults && !loading" data-test="pageContent">
			<Table
				:sourceObject="tableSource"
				@sort="updateDirection"
				@change="updatedPagination"
				:sortOptions="handleSort"
				class="formHistoy__table"
				:isArrowUp="true"
			>
				<template #body-timestamp="{ body }">
					<div>
						{{ fullDateString(body.timestamp) }}
						<div>
							{{ differenceDateString(body.timestamp) }}
						</div>
					</div>
				</template>
				<template #body-action="{ body, index }">
					<span
						class="formHistoy-log-text"
						v-html="formatActionText(body)"
					></span>
					<Button
						v-if="
							body.fieldChanges.items &&
								body.fieldChanges.items.length > 1
						"
						className="btn--link"
						labelText="Details"
						:btnClick="() => displayHistoryDetailModal(index)"
					/>
					<Button
						v-if="isDeleteOrCreate(body)"
						className="btn--link"
						labelText="Details"
						:btnClick="() => displayDetailsModal(body)"
					/>
				</template>
				<template #body-restoreLink="{ body, index }">
					<Button
						className="btn--link"
						labelText="Restore"
						:btnClick="
							() => {
								showModalRestoreForm(index);
							}
						"
						:hidden="body.action === 'Add'"
					/>
				</template>
			</Table>
			<LogHistoryDetailModal
				@close="closeLogHistoryDetailModal"
				:logs="fieldChanges"
				v-if="showLogHistoryDetailModal"
				data-test="logHistoryDetailModal"
			/>
			<LogHistoryObjDetailModal
				:objectDetails="objectDetails"
				v-if="showObjDetailsModal"
				@close="showObjDetailsModal = false"
				data-test="logHistoryObjDetailModal"
			/>
			<ChangeReasonModal
				:title="'Are you sure you want to restore to this revision?'"
				:labelText="'Reason for restoring'"
				:placeholder="'Enter Reason'"
				:required="true"
				:maxLength="500"
				v-model="showConfirmationModal"
				@message="setResultTextArea"
				data-test="changeReasonModal"
			/>
		</ScrollContainer>
		<NoResults v-show="!hasResults && !loading" data-test="noResults" />
	</div>
</template>

<script>
import ActionSetNav from '@/site-components/actions-set/action-set-nav';
import HistoryFilters from './global-history-screen-filters';
import ScrollContainer from '@/components/shells/scroll-container/scroll-container';
import Table from '@/components/shells/table/table';
import ResultCounter from '@/site-components/result-counter/result-counter';
import Breadcrumbs from '@/components/shells/breadcrumbs/breadcrumbs';
import Spinner from '@/components/visual/spinner/spinner';
import Button from '@/components/forms/button/button';
import LogHistoryDetailModal from '@/views/form-designer/log-history-detail-modal';
import LogHistoryObjDetailModal from '@/views/shared/log-history-obj-detail-modal';
import NoResults from '@/site-components/no-results/no-results';
import ChangeReasonModal from '@/views/global-forms/change-reason-modal';
import PageKeyNames from '@/utils/page-key-names';
import _ from 'lodash';

// vuex
import { mapGetters, mapActions, mapState } from 'vuex';

// mixins
import CustomerCardActions from '@/mixins/customers/customer-card-action';
import HistoryHelpers from '@/mixins/history-helpers';
import DateTimeMixin from '@/mixins/datetime-mixin';

// libraries
import { formatDate } from '@/utils/datetime';
import { sub } from 'date-fns';
import {
	removeObjectEmptyValues,
	removePropertiesWithSameValues
} from '@/utils/object';

export default {
	name: 'FormsHistoryScreen',
	mixins: [DateTimeMixin, HistoryHelpers, CustomerCardActions],
	components: {
		ActionSetNav,
		ScrollContainer,
		Table,
		HistoryFilters,
		ResultCounter,
		Breadcrumbs,
		Spinner,
		Button,
		LogHistoryDetailModal,
		LogHistoryObjDetailModal,
		NoResults,
		ChangeReasonModal
	},
	props: {
		customerId: String,
		orderBy: String,
		orderDirection: String,
		search: String,
		minDatetime: String,
		maxDatetime: String,
		changedByUserId: String,
		changedEntityId: [String, Number],
		page: Number,
		countPerPage: Number
	},
	data() {
		return {
			filters: {},
			defaultFilters: {
				customerId: this.$route.params.customerId || undefined,
				orderBy: 'timestamp',
				orderDirection: 'desc',
				search: '',
				minDatetime: formatDate(
					new Date(sub(new Date(), { weeks: 1 }))
				),
				maxDatetime: formatDate(new Date()),
				changedByUserId: undefined,
				SourceApplication: ['VueSupport'],
				entityType: ['FormRepositoryModel'],
				changedEntityId: this.changedEntityId,
				page: 1,
				countPerPage: 10
			},
			customer: undefined,
			defaultUrlNamesCustomers: [
				{
					url: '/clients',
					name: 'Clients'
				},
				{
					url: `/clients/${this.$route.params.customerId}/forms`,
					name: 'Forms'
				},
				{
					name: 'History'
				}
			],
			defaultUrlNamesGlobalForms: [
				{
					url: `/forms`,
					name: 'Global Forms'
				},
				{
					name: 'History'
				}
			],
			navigatingFromCustomer: false,
			formsLogs: [],
			loading: true,
			showConfirmationModal: false,
			indexFormToRestore: -1,
			indexLogSeleted: 0,
			showLogHistoryDetailModal: false,
			restoredMessage: '',
			entityParams: {
				entityType: 'FormRepositoryModel',
				changedEntityId: this.id
			},
			fieldChanges: {},
			pageTitle: '',
			navbarTitle: ''
		};
	},
	async created() {
		if (this.customerId) {
			this.customer = this.customerById(this.customerId);
			this.navigatingFromCustomer = true;
		}
		if (this.navigatingFromCustomer) {
			this.mainNavActions = this.actions;
		}
		this.tableSource = {
			...this.tableSource,
			headers: this.renderHeader()
		};

		try {
			const params = {
				entityType: 'FormRepositoryModel',
				changedEntityId: this.changedEntityId
			};
			await this.getForm(this.changedEntityId);
			await this.getUsers(params);
		} finally {
			this.loading = false;
		}
	},
	mounted() {
		this.$store.commit('setCurrentPage', PageKeyNames.FORM);
	},
	destroyed() {
		this.$store.commit('setCurrentPage', null);
	},
	computed: {
		...mapState('historyLogs', ['historyLogs', 'users']),
		...mapState('form', ['form']),
		...mapGetters('customers', ['customerById']),
		hasResults() {
			return (
				'items' in this.historyLogs &&
				this.historyLogs.items?.length > 0
			);
		},
		resultCounter() {
			return this.historyLogs.items?.length || 0;
		},
		handleSort() {
			return {
				currentSort: this.filters.orderBy,
				currentSortDir: this.filters.orderDirection
			};
		},
		urlNames() {
			const routeBreadcrumb = this.$route.meta.breadcrumb;
			const storeBreadcrumb = this.$store.getters.breadcrumbs(
				this.$route.path
			);

			if (!_.isEmpty(routeBreadcrumb)) {
				return routeBreadcrumb;
			}

			if (!_.isEmpty(storeBreadcrumb)) {
				return storeBreadcrumb;
			}

			if (this.navigatingFromCustomer) {
				return this.defaultUrlNamesCustomers;
			}

			return this.defaultUrlNamesGlobalForms;
		}
	},
	methods: {
		...mapActions('form', ['updateForm', 'getForm']),
		...mapActions('historyLogs', ['getHistoryLogs', 'getUsers']),
		async fetchData() {
			this.loading = true;

			let query = removeObjectEmptyValues(this.filters);

			const filteredQuery = _.cloneDeep(query);
			delete filteredQuery.customerId;

			filteredQuery.recordStart = this.filters.page;
			filteredQuery.maxCount = this.filters.countPerPage;

			delete filteredQuery.page;
			delete filteredQuery.countPerPage;

			if (this.customerId) {
				filteredQuery.DealershipGroupGuid = this.customerId;
			}

			try {
				await this.getHistoryLogs(filteredQuery);
				if (!this.pageTitle) {
					this.pageTitle = this.historyLogs.pageTitle;
					this.navbarTitle = this.historyLogs.navbarTitle;
				}
				if (_.get(this.historyLogs, 'items', []).length > 0) {
					this.formsLogs = [...this.historyLogs.items];
				}

				this.tableSource = {
					...this.tableSource,
					source: this.historyLogs
				};
			} finally {
				this.loading = false;
			}
		},
		updateRoute() {
			let queries = removePropertiesWithSameValues(
				this.filters,
				this.defaultFilters
			);

			queries = removeObjectEmptyValues(queries);

			let path = `/forms/${this.changedEntityId}/history`;

			if (this.navigatingFromCustomer) {
				path = `/clients/${this.customerId}${path}`;
			}

			if (
				this.$route.path !== path ||
				JSON.stringify(this.$route.query) !== JSON.stringify(queries)
			) {
				this.$router.replace({
					path: path,
					query: queries
				});
			}
		},
		renderHeader() {
			return [
				{
					key: 'timestamp',
					label: 'Date/Time',
					sorteable: true
				},
				{
					key: 'action',
					label: 'Action',
					sorteable: false
				},
				{
					key: 'message',
					label: 'Comments',
					sorteable: false
				},
				{
					key: 'changedByUserName',
					label: 'Changed By',
					sorteable: true
				},
				{
					key: 'restoreLink',
					label: 'Version Restore',
					sorteable: false
				}
			];
		},
		updateToast(val) {
			this.$store.dispatch('toastMessages/show', {
				toastId: 'form.history',
				statusCode: val.status,
				message: val.message,
				slot: 'default',
				useIcon: true,
				userDismissable: true
			});
		},
		async restoreLogAction() {
			let formLogByIndex = { ...this.formsLogs[this.indexFormToRestore] };
			// restore to original value each form data.items[0].
			let originalForm = _.clone(formLogByIndex.data.items[0]);

			let form = _.mapKeys(
				_.omit(originalForm, '$type'),
				(value, key) => key.charAt(0).toLowerCase() + key.slice(1)
			);
			form.message = `Restored - ${this.restoredMessage}`;

			await this.updateFormAction(form);
		},

		async updateFormAction(form) {
			await this.updateForm(form)
				.then(resp => {
					let toastmsg = {
						status: resp.statusCode,
						message: 'Form successfully restored.'
					};
					this.updateToast(toastmsg);
					this.fetchData();
					this.showConfirmationModal = false;
					this.restoredMessage = '';
				})
				.catch(error => {
					let val = {
						status: 400,
						message: error ? error : 'Form unsuccessfully restored.'
					};
					this.updateToast(val);
				});
		},
		displayHistoryDetailModal(index) {
			this.indexLogSeleted = index;

			this.fieldChanges = this.tableSource.source.items[
				this.indexLogSeleted
			];

			this.showLogHistoryDetailModal = true;
		},
		showModalRestoreForm(index) {
			this.indexFormToRestore = index;

			if (index !== undefined) {
				this.showConfirmationModal = true;
			}
		},
		closeLogHistoryDetailModal(value) {
			this.showLogHistoryDetailModal = value;
			this.indexLogSeleted = 0;
		},
		setResultTextArea(val) {
			this.restoredMessage = val;
		},
		updateDirection(sortValue) {
			this.filters.orderDirection = sortValue.sortDir;
		},
		updatedPagination(value) {
			this.filters.page = value.page;
			this.filters.countPerPage = value.countPerPage;
		}
	},
	watch: {
		filters: {
			deep: true,
			handler(obj) {
				this.updateRoute(obj);
			}
		},
		restoredMessage: {
			async handler(val) {
				if (val) {
					await this.restoreLogAction();
				}
			}
		},
		'$route.query': {
			immediate: true,
			handler(newQuery) {
				if (newQuery.page) {
					newQuery.page = this.page;
				}

				if (newQuery.countPerPage) {
					const availableCountPerPage = [10, 20, 30, 50, 100];

					if (availableCountPerPage.includes(this.countPerPage)) {
						newQuery.countPerPage = this.countPerPage;
					} else {
						newQuery.countPerPage = this.defaultFilters.countPerPage;
					}
				}

				this.filters = { ...this.defaultFilters, ...newQuery };
				this.fetchData();
			}
		}
	},
	beforeRouteLeave(to, from, next) {
		const actualBreadcrumb =
			from.meta && from.meta.breadcrumb ? [...from.meta.breadcrumb] : [];

		const routePages = [
			'impact-form-edit-designer',
			'laser-form-edit-designer',
			'customer-forms',
			'form-history'
		];

		const isFromHistory =
			from.name === 'form-history' ||
			from.name === 'customer-form-history';
		const isToRoutePage = routePages.includes(to.name);
		const isToFormDesigner =
			to.name === 'laser-form-edit-designer' ||
			to.name === 'impact-form-edit-designer';
		const isCodeManagerToRoutePage =
			to.name === 'code-manager' && routePages.includes(from.name);

		if (isFromHistory && isToRoutePage) {
			let newBreadcrumb = actualBreadcrumb.filter(obj =>
				['Clients', 'Forms', 'Global Forms', 'Code Manager'].includes(
					obj.name
				)
			);

			if (to.name === 'customer-forms') {
				newBreadcrumb = newBreadcrumb.map(obj => {
					if (obj.name === 'Forms') {
						delete obj.url;
					}
					return obj;
				});
			} else if (isToFormDesigner) {
				newBreadcrumb.push({ name: 'Form Designer' });
			}

			to.meta.breadcrumb = newBreadcrumb;
		} else if (!isFromHistory) {
			let newBreadcrumb = actualBreadcrumb.filter(
				obj => obj.name !== 'History'
			);

			if (to.name === 'code-manager') {
				newBreadcrumb = newBreadcrumb.filter(
					obj => obj.name !== 'Form Designer'
				);
			}

			if (to.name === 'customer-forms' || isToFormDesigner) {
				newBreadcrumb.push({ name: 'Forms' });
			}

			to.meta.breadcrumb = newBreadcrumb.map(obj => {
				if (obj.name === 'Forms') {
					delete obj.url;
				}
				return obj;
			});
		}

		if (isCodeManagerToRoutePage) {
			const newBreadcrumb = actualBreadcrumb
				.filter(
					obj =>
						obj.name !== 'Form Designer' && obj.name !== 'History'
				)
				.map(item => {
					if (item.name === 'Code Manager') {
						return { name: item.name };
					}
					return item;
				});

			to.meta.breadcrumb = newBreadcrumb;
		}

		const obj = { path: to.fullPath, breadcrumb: to.meta.breadcrumb };
		this.$store.commit('setBreadCrumbs', obj);

		next();
	}
};
</script>

<style lang="scss" scoped>
/**
  * Forms History Screen
**/
.table-container.formHistoy__table {
	::v-deep .table {
		text-transform: initial;
	}
}

.results-toolbar {
	width: 100%;
}

::v-deep .results-toolbar .results-toolbar__search {
	@include mq($from: md) {
		width: 310px;
	}
	@include mq($from: md-l) {
		width: 288px;
	}
}

.formHistoy-log-text {
	&__bold-piece {
		text-transform: capitalize;
		font-weight: $medium-font-weight;
	}
}

::v-deep .daterangepicker.opensleft {
	left: auto;
	right: -60px;

	@include mq($from: sm-l) {
		right: -329px;
	}
	@include mq($from: md) {
		right: 0px;
	}

	&::before {
		left: auto;
		right: 242px;

		@include mq($from: sm-l) {
			right: 511px;
		}
		@include mq($from: md) {
			right: 182px;
		}
	}
	&::after {
		left: auto;
		right: 243px;

		@include mq($from: sm-l) {
			right: 512px;
		}
		@include mq($from: md) {
			right: 183px;
		}
	}
}

::v-deep .results-toolbar__search {
	@include mq($from: md) {
		width: 250px;
	}
}

::v-deep .modal__body {
	overflow: inherit;
}
</style>
